import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  Download,
  Loader2,
  Scale,
  Save,
  CheckCircle2,
  XCircle,
  Archive,
  History,
  FileText,
  Copy,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminGetLabourAnalysis,
  adminUpdateLabourAnalysis,
  adminRerunLabourAnalysis,
  getCcmaDraft,
  exportLabourAnalysisPdf,
} from "@/lib/api/labour-analyzer.functions";
import { downloadBase64File } from "@/lib/download-base64";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/portal/admin/labour-analyses/$id")({
  head: () => ({
    meta: [{ title: "Admin · Matter review" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminMatterReview,
});

function AdminMatterReview() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const fetchOne = useServerFn(adminGetLabourAnalysis);
  const update = useServerFn(adminUpdateLabourAnalysis);
  const rerun = useServerFn(adminRerunLabourAnalysis);
  const getDraft = useServerFn(getCcmaDraft);
  const exportPdf = useServerFn(exportLabourAnalysisPdf);

  const q = useQuery({
    queryKey: ["admin-labour-analysis", id],
    queryFn: () => fetchOne({ data: { id } }),
  });

  const [notes, setNotes] = useState("");
  const [decisionNotes, setDecisionNotes] = useState("");
  const [editedSummary, setEditedSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [openDraftId, setOpenDraftId] = useState<string | null>(null);
  const [draftBody, setDraftBody] = useState("");

  async function onExportPdf() {
    setExporting(true);
    try {
      const { base64, filename } = await exportPdf({ data: { id } });
      downloadBase64File(base64, filename);
      toast.success("Secure PDF downloaded");
    } catch (e: any) {
      toast.error(e?.message ?? "Export failed");
    } finally {
      setExporting(false);
    }
  }

  async function onRegenerate() {
    setRegenerating(true);
    try {
      await rerun({ data: { id } });
      toast.success("Analysis re-run complete");
      qc.invalidateQueries({ queryKey: ["admin-labour-analysis", id] });
      qc.invalidateQueries({ queryKey: ["admin-labour-analyses"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Re-run failed");
    } finally {
      setRegenerating(false);
    }
  }

  useEffect(() => {
    if (q.data?.row) {
      setNotes(q.data.row.admin_notes ?? "");
      setEditedSummary(
        q.data.row.admin_edits?.summary ?? q.data.row.ai_summary ?? "",
      );
    }
  }, [q.data?.row?.id]);

  async function save(extras: any = {}) {
    setSaving(true);
    try {
      const payload: any = {
        id,
        adminNotes: notes,
        adminEdits: { summary: editedSummary },
        ...extras,
      };
      // Only attach a decision note when an approve/reject is being made.
      if (extras.status === "approved" || extras.status === "rejected") {
        payload.decisionNotes = decisionNotes || null;
      }
      await update({ data: payload });
      toast.success("Saved");
      if (extras.status === "approved" || extras.status === "rejected") {
        setDecisionNotes("");
      }
      qc.invalidateQueries({ queryKey: ["admin-labour-analysis", id] });
      qc.invalidateQueries({ queryKey: ["admin-labour-analyses"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function openDraft(draftId: string) {
    setOpenDraftId(draftId);
    setDraftBody("Loading…");
    try {
      const { draft } = await getDraft({ data: { id: draftId } });
      setDraftBody(draft.content_md);
    } catch (e: any) {
      setDraftBody(`Error: ${e?.message}`);
    }
  }

  if (q.isLoading || !q.data?.row) {
    return (
      <p className="text-sm text-muted-foreground">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
      </p>
    );
  }
  const { row, client, drafts, audit, downloadUrl } = q.data;
  const report = (row.ai_report ?? {}) as any;

  return (
    <div className="space-y-6">
      <Link
        to={"/portal/admin/labour-analyses" as any}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-4 w-4" /> All matters
      </Link>

      <header className="rounded-2xl border border-border bg-gradient-to-br from-navy/[0.04] to-gold/[0.06] p-6 shadow-soft">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold">
          <Scale className="h-4 w-4" /> Admin · Matter
        </div>
        <h1 className="font-display text-2xl text-navy">{row.matter_title}</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          {row.doc_type.replaceAll("_", " ")} · {row.original_filename} ·{" "}
          {new Date(row.created_at).toLocaleString("en-ZA")}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Client: <strong className="text-navy">{client?.full_name ?? "—"}</strong> ·{" "}
          {client?.email ?? "—"} · {client?.phone ?? "—"} · Student №:{" "}
          <span className="font-mono">{row.student_number ?? "—"}</span>
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {downloadUrl && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-navy hover:border-navy"
            >
              <Download className="h-3.5 w-3.5" /> Original document
            </a>
          )}
          {row.ai_report && (
            <button
              type="button"
              onClick={onExportPdf}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 rounded-md border border-navy bg-navy px-3 py-1.5 text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              Download AI report PDF
            </button>
          )}
          <button
            type="button"
            onClick={onRegenerate}
            disabled={regenerating || row.status === "analyzing"}
            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-navy hover:border-navy disabled:opacity-60"
            title="Re-run the AI analysis on the original document"
          >
            {regenerating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Regenerate analysis
          </button>
          <span className="rounded-full bg-muted px-2 py-1 text-muted-foreground">
            Status: <strong className="text-navy">{row.status}</strong>
          </span>
          {typeof row.ai_confidence === "number" && (
            <span className="rounded-full bg-muted px-2 py-1 text-muted-foreground">
              AI confidence: {row.ai_confidence}%
            </span>
          )}
        </div>
      </header>

      <section className="space-y-3 rounded-xl border border-border bg-card p-5 shadow-soft">
        <h3 className="text-sm font-semibold text-navy">Editable summary (sent to client)</h3>
        <Textarea
          value={editedSummary}
          onChange={(e) => setEditedSummary(e.target.value)}
          rows={4}
          maxLength={4000}
        />

        <h3 className="pt-2 text-sm font-semibold text-navy">Internal admin notes</h3>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          maxLength={8000}
          placeholder="Internal notes, mark-up, follow-ups…"
        />

        <h3 className="pt-2 text-sm font-semibold text-navy">
          Decision note (recorded in approval audit trail)
        </h3>
        <Textarea
          value={decisionNotes}
          onChange={(e) => setDecisionNotes(e.target.value)}
          rows={2}
          maxLength={4000}
          placeholder="e.g. Approved — facts align with the dismissal record."
        />

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save()} disabled={saving} className="bg-navy hover:bg-navy/90">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </Button>
          <Button
            onClick={() => save({ status: "approved" })}
            disabled={saving}
            className="bg-emerald-700 hover:bg-emerald-700/90"
          >
            <CheckCircle2 className="mr-2 h-4 w-4" /> Approve & release
          </Button>
          <Button
            onClick={() => save({ status: "rejected" })}
            disabled={saving}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
          >
            <XCircle className="mr-2 h-4 w-4" /> Reject
          </Button>
          <Button
            onClick={() => save({ status: "archived" })}
            disabled={saving}
            variant="outline"
          >
            <Archive className="mr-2 h-4 w-4" /> Archive
          </Button>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-5 shadow-soft">
        <h3 className="mb-3 text-sm font-semibold text-navy">Full AI report (JSON)</h3>
        <pre className="max-h-[400px] overflow-auto rounded-md bg-muted/40 p-3 text-[11px] text-foreground/90">
          {JSON.stringify(report, null, 2)}
        </pre>
      </section>

      {row.extracted_text && (
        <section className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <h3 className="mb-3 text-sm font-semibold text-navy">Extracted text</h3>
          <pre className="max-h-[300px] overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-[11px] text-foreground/90">
            {row.extracted_text.slice(0, 20000)}
          </pre>
        </section>
      )}

      <section className="rounded-xl border border-border bg-card p-5 shadow-soft">
        <h3 className="mb-3 text-sm font-semibold text-navy">
          <FileText className="mr-1 inline h-4 w-4" /> CCMA / hearing drafts ({drafts.length})
        </h3>
        {drafts.length === 0 ? (
          <p className="text-xs text-muted-foreground">No drafts generated yet.</p>
        ) : (
          <ul className="space-y-2">
            {drafts.map((d: any) => (
              <li
                key={d.id}
                className="flex items-center justify-between rounded-md border border-border bg-muted/30 p-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-navy">{d.title}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {new Date(d.created_at).toLocaleString("en-ZA")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openDraft(d.id)}
                  className="rounded-md border border-input bg-background px-3 py-1.5 text-xs text-navy hover:border-navy"
                >
                  View
                </button>
              </li>
            ))}
          </ul>
        )}
        {openDraftId && (
          <div className="mt-3 rounded-md border border-border bg-card p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold text-navy">Draft preview</p>
              <button
                type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(draftBody);
                  toast.success("Copied");
                }}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-navy"
              >
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>
            <pre className="max-h-[400px] overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs">
              {draftBody}
            </pre>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-border bg-card p-5 shadow-soft">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-navy">
            <History className="mr-1 inline h-4 w-4" /> Approval audit trail
          </h3>
          <Link
            to={"/portal/admin/labour-analyses-audit" as any}
            className="text-xs font-semibold text-navy hover:underline"
          >
            View full trail
          </Link>
        </div>
        <ul className="space-y-1 text-xs">
          {audit.map((a: any) => {
            const isDecision = String(a.action).startsWith("decision_");
            const verdict = isDecision ? String(a.action).replace("decision_", "") : null;
            return (
              <li
                key={a.id}
                className={`rounded border px-2 py-1 ${
                  isDecision
                    ? verdict === "approved"
                      ? "border-emerald-200 bg-emerald-50"
                      : "border-rose-200 bg-rose-50"
                    : "border-border bg-muted/20"
                }`}
              >
                <span className="font-mono text-[10px] text-muted-foreground">
                  {new Date(a.created_at).toLocaleString("en-ZA")}
                </span>{" "}
                ·{" "}
                <strong>
                  {isDecision ? verdict!.toUpperCase() : a.action}
                </strong>
                {a.details?.actor_name && (
                  <span className="ml-1 text-muted-foreground">
                    by {a.details.actor_name}
                  </span>
                )}
                {a.details?.decision_notes && (
                  <p className="mt-0.5 text-foreground/80">
                    “{a.details.decision_notes}”
                  </p>
                )}
                {!isDecision && a.details && (
                  <span className="ml-2 text-muted-foreground">
                    {JSON.stringify(a.details).slice(0, 160)}
                  </span>
                )}
              </li>
            );
          })}
          {audit.length === 0 && <p className="text-muted-foreground">No events.</p>}
        </ul>
      </section>
    </div>
  );
}
